/**
 * Цвет системной области — статус-бар установленного iPhone Web App, панель
 * вкладок Safari, адресная строка Chrome на Android — совпадает с цветом
 * шапки текущего экрана (CLIENT_IOS_WEBAPP.md §2.1, §11 п. 1–2).
 *
 * Одного значения на тему мало: до входа страницы стоят на фоне `--color-bg`,
 * в приложении шапки лежат на `--color-bg-elevated`, а просмотрщик медиа —
 * чёрный. С фиксированным белым на iPhone над экраном входа был виден шов:
 * статус-бар белый, страница — тёплый серый; в тёмной теме шов был на каждом
 * экране приложения (шапка `#14161b`, статус-бар `#0e1014`).
 * Значение меняется не чаще раза в 300 мс (§11 п. 2).
 */
import type { RouterHistory } from '@tanstack/react-router'

export type TopSurface = 'page' | 'app' | 'media' | 'brand'

const THROTTLE_MS = 300

/**
 * Запасное значение `--color-brand-screen` — на случай, если токен ещё не
 * прочитался. Тот же литерал стоит в `public/theme.js`: тот скрипт работает до
 * загрузки CSS и взять цвет из токена не может.
 */
const BRAND_SCREEN = '#0029cc'

let surface: TopSurface = 'page'
let mediaDepth = 0
/** Обложки профиля под прозрачной шапкой — последняя открытая главнее (изменение v1.15). */
const covers: string[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let lastApplied = 0

function readToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

function targetColor(): string {
  if (mediaDepth > 0) return '#000000'
  const cover = covers.at(-1)
  if (cover) return cover
  // Стартовый экран A-01 стоит на фирменном синем — системная область того же цвета
  if (surface === 'brand') return readToken('--color-brand-screen') || BRAND_SCREEN
  return readToken(surface === 'app' ? '--color-bg-elevated' : '--color-bg') || '#ffffff'
}

/**
 * Значение переписывается не правкой атрибута, а заменой самого тега: часть
 * мобильных движков считывает `theme-color` один раз и на изменение `content`
 * не реагирует — на удаление и вставку узла реагируют почти все. Первая
 * отрисовка при этом не зависит от скрипта: правильный цвет уже проставлен
 * синхронно в `public/theme.js`.
 */
function apply(): void {
  const color = targetColor()
  const head = document.head
  const fresh = document.createElement('meta')
  fresh.setAttribute('name', 'theme-color')
  fresh.setAttribute('content', color)

  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) meta.remove()
  head.append(fresh)
  lastApplied = Date.now()
}

/** Применить с ограничением частоты: сразу, если прошло 300 мс, иначе — по таймеру. */
export function refreshThemeColor(): void {
  if (typeof document === 'undefined') return
  if (timer) return

  const wait = THROTTLE_MS - (Date.now() - lastApplied)
  if (wait <= 0) {
    apply()
    return
  }
  timer = setTimeout(() => {
    timer = null
    apply()
  }, wait)
}

/** Поверхность под системной областью по пути: до входа — фон страницы, в приложении — шапка. */
export function surfaceForPath(pathname: string): TopSurface {
  if (pathname === '/') return 'brand'
  if (pathname === '/auth' || pathname.startsWith('/onboarding')) return 'page'
  return 'app'
}

export function setTopSurface(next: TopSurface): void {
  if (surface === next) return
  surface = next
  refreshThemeColor()
}

/** Полноэкранный тёмный слой (просмотрщик медиа): пока он открыт, область чёрная. */
export function pushMediaSurface(): () => void {
  mediaDepth += 1
  refreshThemeColor()

  let released = false
  return () => {
    if (released) return
    released = true
    mediaDepth = Math.max(0, mediaDepth - 1)
    refreshThemeColor()
  }
}

/**
 * Обложка профиля (S-01, U-01, U-02, P-04): пока шапка над ней прозрачная,
 * системная область — цвета её верха, иначе над цветной шапкой белая полоса.
 */
export function pushCoverSurface(color: string): () => void {
  covers.push(color)
  refreshThemeColor()

  let released = false
  return () => {
    if (released) return
    released = true
    const index = covers.lastIndexOf(color)
    if (index >= 0) covers.splice(index, 1)
    refreshThemeColor()
  }
}

/** Подписка на маршрут: поверхность меняется вместе с экраном. */
export function startThemeColorSync(history: RouterHistory): () => void {
  setTopSurface(surfaceForPath(history.location.pathname))
  refreshThemeColor()
  return history.subscribe(({ location }) => setTopSurface(surfaceForPath(location.pathname)))
}
